// FormUpdateComponent.jsx - Componente para actualizar formularios con preguntas nuevas
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FaBell, FaCheck, FaSpinner } from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

const FormUpdateComponent = ({ user, formUpdateStatus, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState({ diet: null, workout: null });
  const [answers, setAnswers] = useState({});
  const [measurements, setMeasurements] = useState({
    waist_cm: '',
    hip_cm: '',
    chest_cm: '',
    arm_cm: '',
    thigh_cm: '',
    calf_cm: '',
    body_fat_percentage: ''
  });
  const [submitted, setSubmitted] = useState(false);

  // Las medidas corporales son parte de la asesoría 1 a 1
  const isAdvisoryPlan = ['premium', 'gold', 'vip_gold', 'vip', 'competitivo'].includes(
    (user?.subscription_plan || '').toLowerCase()
  );

  useEffect(() => {
    fetchTemplates();
  }, [user?.subscription_plan]);

  const fetchTemplates = async () => {
    try {
      const planType = user?.subscription_plan || 'premium';
      const [dietRes, workoutRes] = await Promise.all([
        axios.get(`${API}/api/personalized-plans/form-template/diet?plan=${planType}`),
        axios.get(`${API}/api/personalized-plans/form-template/workout?plan=${planType}`)
      ]);
      
      setTemplates({
        diet: dietRes.data,
        workout: workoutRes.data
      });
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Error al cargar las preguntas');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldId, value) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleCheckboxChange = (fieldId, optionValue, checked) => {
    setAnswers(prev => {
      const currentValues = prev[fieldId] || [];
      if (checked) {
        return { ...prev, [fieldId]: [...currentValues, optionValue] };
      } else {
        return { ...prev, [fieldId]: currentValues.filter(v => v !== optionValue) };
      }
    });
  };

  const handleSubmit = async () => {
    // Verificar que se respondieron todas las preguntas obligatorias en actualización
    const allNewFields = [
      ...(formUpdateStatus?.diet_update?.new_fields || []),
      ...(formUpdateStatus?.workout_update?.new_fields || [])
    ];
    
    // Validar campos que son obligatorios en actualizaciones (is_mandatory_on_update)
    // o que están marcados como required
    const mandatoryFields = allNewFields.filter(f => f.is_mandatory_on_update || f.required);
    const missingFields = mandatoryFields.filter(f => {
      const answer = answers[f.id];
      // Verificar si está vacío (string vacío, undefined, null, o array vacío)
      if (answer === undefined || answer === null || answer === '') return true;
      if (Array.isArray(answer) && answer.length === 0) return true;
      return false;
    });
    
    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(f => f.label).join(', ');
      toast.error(`Por favor completa las preguntas obligatorias: ${fieldNames}`);
      return;
    }
    
    setSubmitting(true);
    try {
      // Convertir medidas: sólo enviamos las que tienen un valor válido
      const measurementsPayload = {};
      Object.entries(measurements).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        const num = parseFloat(v);
        if (!isNaN(num)) measurementsPayload[k] = num;
      });

      const response = await axios.post(`${API}/api/personalized-plans/submit-form-update`, {
        email: user.email,
        answers: answers,
        measurements_update: measurementsPayload
      });
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success('¡Formulario actualizado exitosamente!');
        setTimeout(() => {
          onComplete && onComplete();
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting form update:', error);
      toast.error('Error al enviar las respuestas');
    } finally {
      setSubmitting(false);
    }
  };

  // Renderizar un campo según su tipo
  const renderField = (field) => {
    const value = answers[field.id] || '';
    
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Input
            type={field.type === 'email' ? 'email' : 'text'}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="bg-white/10 border-white/20 text-white"
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            min={field.min}
            max={field.max}
            step={field.step}
            className="bg-white/10 border-white/20 text-white"
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="bg-white/10 border-white/20 text-white min-h-[100px]"
          />
        );
      
      case 'select':
        return (
          <Select value={value} onValueChange={(v) => handleInputChange(field.id, v)}>
            <SelectTrigger className="bg-white/10 border-white/20 text-white">
              <SelectValue placeholder="Selecciona una opción" />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((opt) => (
                <SelectItem key={opt.id || opt.value} value={opt.value || opt.id}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || []).map((opt) => (
              <div key={opt.id || opt.value} className="flex items-center gap-2">
                <input
                  type="radio"
                  id={`${field.id}-${opt.value}`}
                  name={field.id}
                  value={opt.value || opt.id}
                  checked={value === (opt.value || opt.id)}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  className="accent-pink-500"
                />
                <Label htmlFor={`${field.id}-${opt.value}`} className="text-gray-200 cursor-pointer">
                  {opt.label}
                </Label>
              </div>
            ))}
          </div>
        );
      
      case 'checkbox':
      case 'multiselect':
        return (
          <div className="space-y-2">
            {(field.options || []).map((opt) => (
              <div key={opt.id || opt.value} className="flex items-center gap-2">
                <Checkbox
                  id={`${field.id}-${opt.value}`}
                  checked={(answers[field.id] || []).includes(opt.value || opt.id)}
                  onCheckedChange={(checked) => handleCheckboxChange(field.id, opt.value || opt.id, checked)}
                />
                <Label htmlFor={`${field.id}-${opt.value}`} className="text-gray-200 cursor-pointer">
                  {opt.label}
                </Label>
              </div>
            ))}
          </div>
        );
      
      case 'scale':
        return (
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-400">
              <span>{field.scaleLabels?.low || field.scaleMin || 1}</span>
              <span>{field.scaleLabels?.high || field.scaleMax || 10}</span>
            </div>
            <Input
              type="range"
              min={field.scaleMin || 1}
              max={field.scaleMax || 10}
              value={value || field.scaleMin || 1}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className="w-full accent-pink-500"
            />
            <div className="text-center text-pink-400 font-bold">{value || field.scaleMin || 1}</div>
          </div>
        );
      
      case 'slider':
        return (
          <div className="space-y-2">
            <Input
              type="range"
              min={field.min || 0}
              max={field.max || 100}
              step={field.step || 1}
              value={value || field.min || 0}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className="w-full accent-pink-500"
            />
            <div className="text-center text-pink-400 font-bold">{value || field.min || 0}</div>
          </div>
        );
      
      case 'time':
        return (
          <Input
            type="time"
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="bg-white/10 border-white/20 text-white"
          />
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="bg-white/10 border-white/20 text-white"
          />
        );
      
      default:
        return (
          <Input
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="bg-white/10 border-white/20 text-white"
          />
        );
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-8 flex items-center justify-center">
          <FaSpinner className="animate-spin text-3xl text-pink-500" />
        </CardContent>
      </Card>
    );
  }

  if (submitted) {
    return (
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-center py-12"
      >
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
          <FaCheck className="text-4xl text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">¡Actualización Completada!</h2>
        <p className="text-gray-400">Tus respuestas han sido guardadas exitosamente.</p>
      </motion.div>
    );
  }

  const allNewFields = [
    ...(formUpdateStatus?.diet_update?.new_fields || []),
    ...(formUpdateStatus?.workout_update?.new_fields || [])
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="bg-white/5 border-white/10 overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="flex items-center gap-3">
            <FaBell className="text-2xl" />
            <div>
              <CardTitle className="text-xl">Actualiza tu Formulario</CardTitle>
              <CardDescription className="text-white/80">
                Hay {formUpdateStatus?.total_new_questions || 0} preguntas nuevas para ti
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {allNewFields.length === 0 && !isAdvisoryPlan ? (
            <p className="text-gray-400 text-center py-8">
              No hay preguntas nuevas en este momento.
            </p>
          ) : (
            <>
              {allNewFields.length === 0 && isAdvisoryPlan && (
                <p className="text-gray-400 text-sm">
                  No hay preguntas nuevas este mes. Puedes actualizar tus medidas y % de grasa si lo deseas.
                </p>
              )}
              {allNewFields.map((field, index) => {
                const isMandatory = field.is_mandatory_on_update || field.required;
                return (
                <motion.div
                  key={field.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`space-y-2 p-4 rounded-lg ${isMandatory ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-white/5'}`}
                >
                  <Label className="text-white flex items-center gap-2 flex-wrap">
                    {field.label}
                    {isMandatory && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/30 text-purple-300">
                        Obligatorio
                      </span>
                    )}
                  </Label>
                  {field.helpText && (
                    <p className="text-xs text-gray-400">{field.helpText}</p>
                  )}
                  {renderField(field)}
                </motion.div>
              )})}

              {/* ── Actualizar medidas y % de grasa (opcional) — sólo asesoría 1 a 1 ── */}
              {isAdvisoryPlan && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 }}
                  className="space-y-3 p-4 rounded-lg bg-white/5 border border-white/10"
                  data-testid="update-measurements-block"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-white text-base font-semibold">Actualizar medidas y % de grasa</Label>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-300">
                      Opcional
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">
                    Sólo llena los campos que quieras actualizar este mes. Los que dejes en blanco no se modifican.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { key: 'waist_cm', label: 'Cintura (cm)', placeholder: '70' },
                      { key: 'hip_cm', label: 'Cadera (cm)', placeholder: '95' },
                      { key: 'chest_cm', label: 'Pecho (cm)', placeholder: '85' },
                      { key: 'arm_cm', label: 'Brazo (cm)', placeholder: '30' },
                      { key: 'thigh_cm', label: 'Muslo (cm)', placeholder: '55' },
                      { key: 'calf_cm', label: 'Pantorrilla (cm)', placeholder: '36' }
                    ].map(f => (
                      <div key={f.key}>
                        <Label className="text-gray-200 text-sm">{f.label}</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder={f.placeholder}
                          value={measurements[f.key]}
                          onChange={(e) => setMeasurements(prev => ({ ...prev, [f.key]: e.target.value }))}
                          className="bg-white/10 border-white/20 text-white"
                          data-testid={`update-${f.key.replace('_', '-')}-input`}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label className="text-gray-200 text-sm">% Grasa corporal</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="1"
                      max="60"
                      placeholder="Ej. 22"
                      value={measurements.body_fat_percentage}
                      onChange={(e) => setMeasurements(prev => ({ ...prev, body_fat_percentage: e.target.value }))}
                      className="bg-white/10 border-white/20 text-white"
                      data-testid="update-body-fat-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Si no tienes este dato no te preocupes, pero te recomiendo hacerte una medición para obtener resultados más exactos.
                    </p>
                  </div>
                </motion.div>
              )}

              <div className="pt-6 border-t border-white/10">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full py-3 text-white font-bold rounded-xl"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), #D41C70)' }}
                  data-testid="submit-form-update-btn"
                >
                  {submitting ? (
                    <><FaSpinner className="animate-spin mr-2" /> Guardando...</>
                  ) : (
                    'Guardar Respuestas'
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default FormUpdateComponent;
